// src/net/inputCodec.ts — `InputFrame` TEL FORMATI: kuantalama + ikili kodlama (NETWORK.md §3.2).
//
// Neden `src/net/` ayrı bir paket: sunucu (headless Node) ve istemci AYNI kodu kullanmalı; bu ağaç `src/core` ve
// `src/config` dışında hiçbir şeye bağlı değil, three'siz, DOM'suz.
//
// ── KUANTALAMA: neden ve NE ZAMAN ──
// `moveX/moveZ/aimYaw` float64. Telde 8 + 8 + 16 bit taşınıyor. Eğer istemci SİMÜLASYONU ham float ile koşup telde
// kuantalanmışı gönderirse, sunucu farklı bir girdiyle koşar ve fark ilk tick'te başlar. Bu yüzden kural şu:
//
//   **Kuantalanmış değer sim'e giren değerdir.** `quantizeInput` girdiyi `world.inputs`a YAZILMADAN ÖNCE uygular;
//   telde taşınan da, hash'lenen de aynı sayıdır. `encode(decode(x)) === x` ve `quantize(quantize(x)) === quantize(x)`
//   (idempotent) — testler bunu kilitler.
//
// His etkisi yok: çubuk zaten 0.18 ölü bölgeli ve |v| ≤ 1'e kırpılıyor; 8 bit → 1/127 ≈ 0.008 adım, pad'in kendi
// gürültüsünün altında. `aimYaw` 16 bit → 2π/65535 ≈ 0.0001 rad (0.0055°), nişan yardımı konisi ±60°.
//
// ── KARE DÜZENİ (8 bayt / oyuncu / tick) ──
//   0: moveX    int8    round(clamp(x,−1,1) · 127)
//   1: moveZ    int8
//   2: buttons  uint8   basılı Btn bitleri
//   3: pressed  uint8   bu tick basılan (kenar)  — `buttons`tan TÜRETİLEMEZ
//   4: released uint8   bu tick bırakılan (kenar) — aynı sebeple ayrı taşınır
//   5: flags    uint8   bit0 = aimYaw geçerli (NaN sentinel'in telde karşılığı), bit1 = fromMouse
//   6-7: aimYaw uint16  LE; [−π, π) → 0..65535 (flags bit0 = 0 ise anlamsız, 0 yazılır)
// 8 oyuncu × 60 Hz = 3.8 KB/s. Karşılaştırma: tam durum 8 dövüşçüde 21 KB → 60 Hz'de ~360 KB/s.
import type { InputFrame } from '../core/types.ts';

/** Bir oyuncunun bir tick'lik girdisinin tel boyutu. */
export const INPUT_BYTES = 8;

const MOVE_SCALE = 127;                 // int8'in güvenli tam aralığı (−127..127; −128 kullanılmaz)
// 65536 (65535 DEĞİL): bölen tam güç olunca 0 rad tam ortaya (q = 32768) düşer ve BİREBİR geri gelir.
// 65535 ile 0 rad → 4.8e−5 rad'a kayıyordu; kuantalama tutarlıydı ama "nişan yok" ile "tam ileri" arasındaki
// sıfır noktasının kendine dönmemesi tel formatında istenmeyen bir asimetri.
const YAW_DIV = 65536;
const YAW_MAX = 65535;                  // uint16 tavanı; u → 1 sınırında kırpılır (tek kod aliaslanır, hata < yarım adım)
const TAU = Math.PI * 2;

const FLAG_AIM_VALID = 1;
const FLAG_FROM_MOUSE = 2;

/** [−π, π) aralığına sarar. `core/vec3.ts` `wrapAngle` ile aynı; net paketi core dışında hiçbir şeye bağlanmasın diye yerel. */
function wrap(a: number): number {
  a = a % TAU;
  if (a > Math.PI) a -= TAU;
  else if (a <= -Math.PI) a += TAU;
  return a;
}

/**
 * [−1, 1]'e kırpar ve SONLU OLMAYANI temizler.
 * NaN neden ayrı ele alınıyor: `NaN > 1` ve `NaN < -1` ikisi de false → kırpma NaN'i olduğu gibi geçiriyordu.
 * `quantizeInput` NaN bırakıyor, `encodeInput` ise `NaN & 0xff` = 0 yazıyordu → istemcinin sim'i NaN, sunucununki
 * 0 görüyordu: aynı tick, iki farklı dünya. (±Infinity zaten kırpmaya takılıyor.) Ölçüldü ve testle kilitlendi.
 * Kaynağı gerçek: sıfır uzunluklu bir vektörün normalizasyonu (girdi ya da bot algısı) NaN üretebilir.
 */
const clamp1 = (v: number): number => (Number.isNaN(v) ? 0 : v > 1 ? 1 : v < -1 ? -1 : v);

/** Yaw → 0..65535 (geçersiz/NaN çağıran tarafından ele alınır). */
export function encodeYaw(yaw: number): number {
  const u = (wrap(yaw) + Math.PI) / TAU;          // 0..1 (π tam olarak −π'ye sarıldığı için 1'e ulaşmaz)
  const q = Math.round(u * YAW_DIV);
  return q < 0 ? 0 : q > YAW_MAX ? YAW_MAX : q;
}
/** 0..65535 → [−π, π). `+ 0`: −0'ı +0'a çevirir (aşağıdaki nota bak). */
export const decodeYaw = (q: number): number => (q / YAW_DIV) * TAU - Math.PI + 0;

/** Çubuk bileşeni → −127..127. */
export const encodeMove = (v: number): number => Math.round(clamp1(v) * MOVE_SCALE);
/**
 * −127..127 → −1..1. `q === 0 ? 0 : ...` NEGATİF SIFIRI temizler ve bu bir determinizm düzeltmesidir:
 * `Math.round(-0.003 * 127)` → `-0`, `-0 / 127` → `-0`. `-0 === 0` doğru olduğu için gözle görünmez, ama
 * `-0`'ın BİT DESENİ farklıdır ve `world.hash()` float64 bitlerinden karıştırıyor. Telde `-0` da `+0` da
 * `0x00` olarak gider → çözülünce `+0` döner. Kuantalama bu normalleştirmeyi yapmazsa istemcinin dünyası `-0`,
 * sunucununki `+0` taşır: aynı görünen, farklı hash'lenen iki dünya. (test/net.test.ts bunu kilitler.)
 */
export const decodeMove = (q: number): number => (q === 0 ? 0 : q / MOVE_SCALE);

/**
 * Girdiyi TELDE taşınacak değerlere indirger — YERİNDE, tahsissiz.
 * `world.inputs[id]`e yazılmadan ÖNCE çağrılır: sim'in gördüğü sayı ile telde giden sayı aynı olsun.
 * İdempotent: ikinci çağrı hiçbir şeyi değiştirmez (sunucu güvenle yeniden uygulayabilir).
 */
export function quantizeInput(f: InputFrame): void {
  f.moveX = decodeMove(encodeMove(f.moveX));
  f.moveZ = decodeMove(encodeMove(f.moveZ));
  // NaN bir SENTINEL ("nişan geçersiz"), sayı değil. ±Infinity de telde taşınamaz → aynı sentinel'e düşer;
  // `Number.isFinite` ikisini birden ele alıyor (`!Number.isNaN` Infinity'yi geçiriyordu).
  f.aimYaw = Number.isFinite(f.aimYaw) ? decodeYaw(encodeYaw(f.aimYaw)) : NaN;
  f.buttons &= 0xff; f.pressed &= 0xff; f.released &= 0xff;
}

/** Kareyi `buf`a `off` ofsetinden yazar; yazılan bayt sayısını döner (her zaman INPUT_BYTES). Tahsissiz. */
export function encodeInput(f: InputFrame, buf: Uint8Array, off: number): number {
  const valid = Number.isFinite(f.aimYaw);   // NaN ve ±Infinity: "nişan geçersiz"
  buf[off] = encodeMove(f.moveX) & 0xff;
  buf[off + 1] = encodeMove(f.moveZ) & 0xff;
  buf[off + 2] = f.buttons & 0xff;
  buf[off + 3] = f.pressed & 0xff;
  buf[off + 4] = f.released & 0xff;
  buf[off + 5] = (valid ? FLAG_AIM_VALID : 0) | (f.fromMouse ? FLAG_FROM_MOUSE : 0);
  const q = valid ? encodeYaw(f.aimYaw) : 0;
  buf[off + 6] = q & 0xff;
  buf[off + 7] = (q >>> 8) & 0xff;
  return INPUT_BYTES;
}

/** `buf`taki kareyi `out`a çözer (out YERİNDE yazılır, tahsis yok); okunan bayt sayısını döner. */
export function decodeInput(buf: Uint8Array, off: number, out: InputFrame): number {
  // int8'e genişlet: 0x80..0xff → negatif.
  const mx = buf[off]!, mz = buf[off + 1]!;
  out.moveX = decodeMove(mx > 127 ? mx - 256 : mx);
  out.moveZ = decodeMove(mz > 127 ? mz - 256 : mz);
  out.buttons = buf[off + 2]!;
  out.pressed = buf[off + 3]!;
  out.released = buf[off + 4]!;
  const flags = buf[off + 5]!;
  out.fromMouse = (flags & FLAG_FROM_MOUSE) !== 0;
  out.aimYaw = (flags & FLAG_AIM_VALID) !== 0 ? decodeYaw(buf[off + 6]! | (buf[off + 7]! << 8)) : NaN;
  return INPUT_BYTES;
}
